import { useEffect, useState } from 'react';
import { useForm } from '../../../../hooks/useForm';
import { NOTIFICATION_VARIANT } from '../../../../constants/types';
import { useNotifications } from '../../../../Providers/NotificationsProvider/NotificationsProvider';
import { isValidMail } from '../../../../utils/utils';
import { REBATE_TYPE } from './interfaces';

const getInitialState = (payoutConfig) => ({
  taxId: { value: payoutConfig.taxId, required: false },
  taxName: { value: payoutConfig.taxName, required: false },
  vatStatus: {
    value: payoutConfig.vatStatus,
    required: ({ defaultRebateType }) => defaultRebateType !== REBATE_TYPE.NONE,
  },
  defaultRebateType: { value: payoutConfig.defaultRebateType || 'NONE', required: true },
  defaultRebatePercentage: {
    value: payoutConfig.defaultRebatePercentage,
    required: false,
    type: 'float',
    minValue: 0,
    maxValue: 100,
  },
  defaultRevenueSharePercentage: {
    value: payoutConfig.defaultRevenueSharePercentage,
    required: false,
    type: 'float',
    minValue: 0,
    maxValue: 100,
  },
  settlementRecipientEmails: {
    value: payoutConfig.settlementRecipientEmails || [],
    required: false,
    customValidation: (value) => {
      if (!value || value.length === 0) return '';
      const invalidEmail = value.find((email) => !isValidMail(email));
      if (invalidEmail) return `El email "${invalidEmail}" no es válido.`;
      return '';
    },
  },
});

export const useProducerPayoutConfigForm = (payoutConfig, onSubmit) => {
  const { showNotification } = useNotifications();
  const {
    formState,
    formValues,
    handleInputChange,
    validateFormInputs,
    handleMultipleInputsChange,
  } = useForm(getInitialState(payoutConfig), [payoutConfig]);

  // Email input state for multi-email field
  const [emailInput, setEmailInput] = useState('');

  useEffect(() => {
    setEmailInput('');
  }, [payoutConfig]);

  const handleEmailAdd = () => {
    const email = emailInput.trim();
    if (email && !formValues.settlementRecipientEmails.includes(email)) {
      handleInputChange({
        target: {
          name: 'settlementRecipientEmails',
          value: [...formValues.settlementRecipientEmails, email],
        },
      });
      setEmailInput('');
    }
  };

  const handleEmailDelete = (emailToDelete) => {
    handleInputChange({
      target: {
        name: 'settlementRecipientEmails',
        value: formValues.settlementRecipientEmails.filter((e) => e !== emailToDelete),
      },
    });
  };

  const handleRebateTypeChange = ({ target: { value } }) => {
    switch (value) {
      case REBATE_TYPE.NONE:
        handleMultipleInputsChange([
          { name: 'defaultRebateType', value },
          { name: 'vatStatus', value: null },
          { name: 'defaultRebatePercentage', value: null },
          { name: 'defaultRevenueSharePercentage', value: null },
        ]);
        break;
      case REBATE_TYPE.PERCENTAGE:
        handleMultipleInputsChange([
          { name: 'defaultRebateType', value },
          { name: 'defaultRevenueSharePercentage', value: null },
        ]);
        break;
      case REBATE_TYPE.REVENUE_SHARE:
        handleMultipleInputsChange([
          { name: 'defaultRebateType', value },
          { name: 'defaultRebatePercentage', value: null },
        ]);
        break;
      default:
        break;
    }
  };

  const handleSubmit = () => {
    const isValid = validateFormInputs();
    if (isValid) {
      onSubmit({ id: payoutConfig.id, ...formValues });
    } else {
      showNotification('Hay errores en el formulario', NOTIFICATION_VARIANT.ERROR);
    }
  };

  return {
    formState,
    formValues,
    handleInputChange,
    validateFormInputs,
    emailInput,
    setEmailInput,
    handleEmailAdd,
    handleEmailDelete,
    handleRebateTypeChange,
    handleSubmit,
  };
};
